// ─────────────────────────────────────────────────────────────────────────────
// useModalColors — the modal/panel colour kit (≈412 consumers)
//
// TWO exports, and picking the right one matters:
//
//   useModalColors()  →  CSS variables. Mode-aware (flips light/dark with the
//                        colour-mode class). Use this in React components.
//
//   MODAL_TOKENS      →  literal hex, DARK values only. Use ONLY where a CSS
//                        variable cannot resolve — in practice that means SVG.
//                        Recharts passes `fill`/`stroke` through as SVG
//                        ATTRIBUTES, and `fill="var(--x)"` is not a colour to
//                        an SVG attribute: the shape renders black or not at
//                        all. Module-scope constants (evaluated before React)
//                        are the other case.
//
// BOTH now derive from the active preset (`theme/presets/index.js`), so the
// whole kit re-skins when the preset changes. Previously MODAL_TOKENS was a
// hand-maintained copy that had drifted badly: retired greys (#808080,
// #B0B0B0), pure #FFFFFF where the theme deliberately uses #F5F5F7, an old
// danger red (#EE5D50), and an orphan orange (#E6890A) that matched nothing in
// the palette and had spread to ~57 files through this file alone.
//
// ⚠ Never add a raw hex here. Add it to the preset.
// ─────────────────────────────────────────────────────────────────────────────

import preset from 'theme/presets';

/** '#FF9500' + 0.35 → 'rgba(255, 149, 0, 0.35)' */
const alpha = (hex, a) => {
	const c = hex.replace('#', '');
	const r = parseInt(c.slice(0, 2), 16);
	const g = parseInt(c.slice(2, 4), 16);
	const b = parseInt(c.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const S = preset.surfaceScale; // dark elevation ramp
const INK = preset.ink;
const A = preset.accent;
const F = preset.feedback;

/**
 * The brand gradient used by modal headers and primary CTAs.
 * Built from the preset so it can never drift from the accent again — this is
 * where the orphan #E6890A used to live.
 */
const ACCENT_GRADIENT = `linear-gradient(135deg, ${A.light} 0%, ${A.primary} 50%, ${A.dark} 100%)`;
const ACCENT_GRADIENT_HOVER = `linear-gradient(135deg, ${A.light} 0%, ${A.primary} 60%, ${A.dark} 100%)`;

// ── Raw token values — literal hex, dark values. SVG-safe. ───────────────────
export const MODAL_TOKENS = {
	// Surfaces
	bg: S[800], // main modal body
	bgDeep: S[900], // footer / nested panels
	bgInput: S[700], // form field background
	bgInputHover: S[600], // form field hover

	// Header (brand gradient)
	headerBg: ACCENT_GRADIENT,
	headerText: A.onAccent, // near-black on the accent — never white
	headerSubText: 'rgba(0,0,0,0.6)',

	// View modal
	viewBg: S[900],
	viewBodyBg: S[900],
	viewHeaderBg: S[700],
	viewHeaderText: INK.dPrimary,
	viewHeaderBorder: S[600],
	viewFooterBg: S[800],
	viewFooterBorder: S[600],
	viewButtonBg: S[700],
	viewButtonHoverBg: S[600],
	viewButtonText: INK.dPrimary,
	white: '#FFFFFF',

	// Close button (sits inside the accent header)
	closeBtnBg: 'rgba(0,0,0,0.15)',
	closeBtnColor: A.onAccent,
	closeBtnHoverBg: 'rgba(0,0,0,0.28)',

	// Borders
	borderColor: S[600],
	borderFocus: A.primary,
	divider: S[700],

	// Labels & text
	labelColor: INK.dMuted,
	bodyText: INK.dSecondary,
	headingText: INK.dPrimary,
	mutedText: INK.dMuted,

	// Primary button (accent CTA)
	primaryBtnBg: ACCENT_GRADIENT,
	primaryBtnText: A.onAccent,
	primaryBtnHoverBg: ACCENT_GRADIENT_HOVER,
	primaryBtnShadow: `0 0 16px ${alpha(A.primary, 0.35)}`,
	primaryBtnActiveBg: A.dark,

	// Secondary button (ghost)
	secondaryBtnBg: 'transparent',
	secondaryBtnText: INK.dSecondary,
	secondaryBtnBorder: S[500],
	secondaryBtnHoverBg: S[700],
	secondaryBtnHoverText: INK.dPrimary,

	// Danger / destructive
	dangerBtnBg: F.dangerD,
	dangerBtnText: '#FFFFFF',
	dangerBtnHoverBg: alpha(F.dangerD, 0.12),
	dangerBtnBorder: alpha(F.dangerD, 0.35),

	// Footer
	footerBg: S[900],
	footerBorder: S[600],

	// Overlay / backdrop
	overlayBg: 'rgba(0, 0, 0, 0.65)',

	// Status badges
	badgeSuccessBg: alpha(F.successD, 0.12),
	badgeSuccessText: F.successD,
	badgeSuccessBorder: alpha(F.successD, 0.3),

	badgeWarningBg: alpha(F.warningD, 0.12),
	badgeWarningText: F.warningD,
	badgeWarningBorder: alpha(F.warningD, 0.3),

	badgeErrorBg: alpha(F.dangerD, 0.12),
	badgeErrorText: F.dangerD,
	badgeErrorBorder: alpha(F.dangerD, 0.3),

	badgeInfoBg: alpha(F.infoD, 0.25),
	badgeInfoText: F.infoD,
	badgeInfoBorder: alpha(F.infoD, 0.35),

	// Accent
	accentGold: A.primary,
	// Accent-coloured TEXT (see the hook below). Dark-mode literal.
	accentText: A.primary,
	goldLight: A.light,
	goldDark: A.dark,

	// Box shadows
	modalShadow: '0px 20px 60px rgba(0,0,0,0.6)',
	cardShadow: '0px 10px 30px rgba(0,0,0,0.4)',
};

/**
 * Colours for a native `<option>`, as LITERALS for the given colour mode.
 *
 * ★ THE DROPDOWN LIST IS DRAWN BY THE BROWSER, NOT BY THE PAGE. `var(--…)` in an
 * option's style (what `useModalColors()` hands out) is not reliably resolved
 * there, so on a dark screen the list could come out light text on a white
 * popup — the "currency names not showing" bug in the Close Deal modal. Pair it
 * with `colorScheme: colorMode` on the `<Select>` so the popup chrome
 * (scrollbar, highlight) matches too. `colorMode` comes from Chakra's
 * `useColorMode()`, the source the theme toggle writes.
 */
export const nativeOptionStyle = (colorMode) =>
	colorMode === 'dark'
		? { background: S[700], color: INK.dPrimary }
		: { background: preset.paper[0], color: INK.lPrimary };

// ─────────────────────────────────────────────────────────────────────────────
// Chakra-ready hook — mode-aware CSS variable references from the single root
// theme. `var(--chakra-colors-*)` works in Chakra props AND raw `style={{}}`
// (used to colour native <option>s) and flips with the colour-mode class.
// Values that are brand-constant (the accent gradient, near-black on accent)
// stay literal — they are the same in both modes by design.
// ─────────────────────────────────────────────────────────────────────────────
const cv = (name) => `var(--chakra-colors-${name})`; // colour token → CSS var
const sv = (name) => `var(--chakra-shadows-${name})`; // shadow token → CSS var

export const useModalColors = () => ({
	// Surfaces — mode-aware
	bg: cv('bg-surface'),
	bgDeep: cv('bg-subtle'),
	bgInput: cv('bg-input'),
	bgInputHover: cv('bg-elevated'),

	// Header (brand gradient — mode-independent)
	headerBg: ACCENT_GRADIENT,
	headerText: A.onAccent,
	headerSubText: 'rgba(0,0,0,0.65)',

	// View-modal header/body/footer — mode-aware
	viewBg: cv('bg-surface'),
	viewBodyBg: cv('bg-base'),
	viewHeaderBg: cv('bg-elevated'),
	viewHeaderText: cv('text-primary'),
	viewHeaderBorder: cv('border-default'),
	viewFooterBg: cv('bg-subtle'),
	viewFooterBorder: cv('border-default'),
	viewButtonBg: cv('bg-elevated'),
	viewButtonHoverBg: cv('border-default'),
	viewButtonText: cv('text-primary'),
	white: '#FFFFFF',

	// Close button (sits on the accent header — mode-independent)
	closeBtnBg: 'rgba(0,0,0,0.15)',
	closeBtnColor: A.onAccent,
	closeBtnHoverBg: 'rgba(0,0,0,0.28)',

	// LABELLED action button on the accent header (e.g. "Lead Notes").
	// Mode-independent for the same reason as the close button: the header is
	// the brand orange in both modes.
	//
	// It deliberately does NOT reuse closeBtnBg. That 15%-black wash is right
	// for a bare icon affordance, but under a text label it reads as flat
	// header decoration rather than something pressable. A near-solid light
	// fill plus a defined edge gives it button weight; the ink stays
	// `headerText` (near-black), which is ~17:1 on this fill.
	headerBtnBg: 'rgba(255,255,255,0.92)',
	headerBtnHoverBg: 'rgba(255,255,255,1)',
	headerBtnBorder: 'rgba(0,0,0,0.18)',
	headerBtnHoverBorder: 'rgba(0,0,0,0.32)',
	headerBtnActiveBg: 'rgba(255,255,255,0.85)',

	// Borders — mode-aware
	borderColor: cv('border-default'),
	borderFocus: cv('border-focus'),
	divider: cv('border-subtle'),

	// Text — mode-aware
	labelColor: cv('text-muted'),
	bodyText: cv('text-secondary'),
	headingText: cv('text-primary'),
	mutedText: cv('text-muted'),

	// Primary button (accent CTA — mode-independent)
	primaryBtnBg: ACCENT_GRADIENT,
	primaryBtnText: A.onAccent,
	primaryBtnHoverBg: ACCENT_GRADIENT_HOVER,
	primaryBtnShadow: MODAL_TOKENS.primaryBtnShadow,
	primaryBtnActiveBg: cv('accent-pressed'),

	// Secondary button (ghost) — mode-aware
	secondaryBtnBg: 'transparent',
	secondaryBtnText: cv('text-secondary'),
	secondaryBtnBorder: cv('border-default'),
	secondaryBtnHoverBg: cv('bg-elevated'),
	secondaryBtnHoverText: cv('text-primary'),

	// Danger button — mode-aware semantic.
	//
	// The hover pairing is the trap here: hovering used to swap the SOLID
	// danger fill for the pale `danger-subtle` tint while the text stayed
	// #FFFFFF — white on near-white in light mode. On hover the fill must stay
	// solid (darken via filter at the call-site if a hover delta is wanted) and
	// the text must stay on the solid fill.
	dangerBtnBg: cv('danger'),
	dangerBtnText: '#FFFFFF',
	dangerBtnHoverBg: cv('danger'),
	dangerBtnBorder: cv('danger'),
	// Subtle tint for danger BACKGROUNDS (icon wells, row washes) — never as a
	// hover state under white text.
	dangerSubtleBg: cv('danger-subtle'),

	// Footer — mode-aware
	footerBg: cv('bg-subtle'),
	footerBorder: cv('border-default'),

	// Accent — mode-aware token + brand shades
	//
	// `accentGold` is the brand FILL (#FF9500 in both modes). It is not an ink:
	// on a light surface it lands at 2.2:1 and is unreadable. Use it for
	// backgrounds, borders, icons and bars.
	//
	// For accent-coloured TEXT on a normal surface use `accentText`, which
	// darkens to #A85600 on light (5.3:1) and stays vivid on dark (7.85:1).
	accentGold: cv('accent-gold'),
	accentText: cv('text-accent'),
	// Text placed ON an accent-filled element. Use INSTEAD of headerText when
	// the fill comes from a token rather than the gradient.
	onAccentText: cv('text-onAccent'),
	goldLight: cv('accent-goldLight'),
	goldDark: cv('accent-goldDark'),
	// Accent focus/hover glow. SearchBar and friends were already reading
	// `colors.goldGlow`; the key never existed, so those focus rings silently
	// rendered nothing (boxShadow: undefined).
	goldGlow: `var(--chakra-shadows-goldGlow)`,

	// Overlay — mode-aware scrim
	overlayBg: cv('bg-overlay'),

	// Status badges — mode-aware semantic tokens (AA in both modes)
	badgeSuccessBg: cv('success-subtle'),
	badgeSuccessText: cv('success'),
	badgeSuccessBorder: cv('success'),
	badgeWarningBg: cv('warning-subtle'),
	badgeWarningText: cv('warning'),
	badgeWarningBorder: cv('warning'),
	badgeErrorBg: cv('danger-subtle'),
	badgeErrorText: cv('danger'),
	badgeErrorBorder: cv('danger'),
	badgeInfoBg: cv('info-subtle'),
	badgeInfoText: cv('info'),
	badgeInfoBorder: cv('info'),

	// Shadows — mode-aware shadow tokens
	modalShadow: sv('deep'),
	cardShadow: sv('card'),
});
